import React, { useState, useEffect, useRef } from "react";
import {
  FaLinkedin,
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaEnvelope,
  FaCopy,
  FaCheck,
  FaTimes,
  FaSlidersH,
  FaExternalLinkAlt
} from "react-icons/fa";
import { FaXTwitter, FaThreads } from "react-icons/fa6";
import {
  getShareBaseUrl,
  getDefaultUtmParams,
  buildFullShareUrl,
  trackShareActionApi,
  getPlatformShareIntent
} from "../../utils/shareTracking";
import "./ShareModal.css";

const PLATFORMS = [
  { id: "linkedin", name: "LinkedIn", icon: <FaLinkedin />, color: "#0a66c2", bg: "rgba(10, 102, 194, 0.12)" },
  { id: "whatsapp", name: "WhatsApp", icon: <FaWhatsapp />, color: "#25d366", bg: "rgba(37, 211, 102, 0.12)" },
  { id: "x", name: "X (Twitter)", icon: <FaXTwitter />, color: "#1da1f2", bg: "rgba(29, 161, 242, 0.12)" },
  { id: "facebook", name: "Facebook", icon: <FaFacebook />, color: "#1877f2", bg: "rgba(24, 119, 242, 0.12)" },
  { id: "threads", name: "Threads", icon: <FaThreads />, color: "#9333ea", bg: "rgba(147, 51, 234, 0.12)" },
  { id: "instagram", name: "Instagram", icon: <FaInstagram />, color: "#e4405f", bg: "rgba(228, 64, 95, 0.12)" },
  { id: "email", name: "Email", icon: <FaEnvelope />, color: "#ea4335", bg: "rgba(234, 67, 53, 0.12)" },
  { id: "copy_link", name: "Copy Link", icon: <FaCopy />, color: "#0284c7", bg: "rgba(2, 132, 199, 0.12)" }
];

const ShareModal = ({ isOpen, onClose, project, item, contentType = "project" }) => {
  const [selectedPlatform, setSelectedPlatform] = useState("copy_link");
  const [baseUrl, setBaseUrl] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [showAdvancedUtm, setShowAdvancedUtm] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [noticeMessage, setNoticeMessage] = useState("");
  const [noticeType, setNoticeType] = useState("info"); // 'success' | 'info' | 'error'
  const [utmParams, setUtmParams] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_id: "",
    utm_content: "",
    utm_term: "",
    utm_source_platform: "",
    utm_creative_format: "",
    utm_marketing_tactic: ""
  });

  const modalRef = useRef(null);
  const initialFocusRef = useRef(null);

  // Normalize content object: supports project, blog, and event (passed via item or project)
  const content = item || project;
  const isEvent =
    contentType === "event" ||
    content?.contentType === "event" ||
    content?.type === "event" ||
    (content?.path && content.path.toLowerCase().includes("/events"));
  const isBlog =
    !isEvent &&
    (contentType === "blog" ||
      content?.contentType === "blog" ||
      content?.type === "blog" ||
      (content?.path && content.path.toLowerCase().includes("/blog")));

  // Initialize Base URL and defaults when modal opens
  useEffect(() => {
    if (!isOpen || !content) return;

    let isMounted = true;
    getShareBaseUrl().then((url) => {
      if (isMounted) setBaseUrl(url);
    });

    const initialPlatform = "copy_link";
    setSelectedPlatform(initialPlatform);
    const defaults = getDefaultUtmParams(initialPlatform, {
      ...content,
      contentType: isEvent ? "event" : isBlog ? "blog" : "project"
    });
    setUtmParams(defaults);
    setShareMessage(
      isEvent
        ? `Check out "${content.title || "this event"}" on Praveen's Portfolio Events!`
        : isBlog
        ? `Check out "${content.title || "this blog post"}" on Praveen's Portfolio Blog!`
        : `Check out ${content.title || "this project"} by Praveen Kumar B!`
    );
    setCopySuccess(false);
    setNoticeMessage("");

    return () => {
      isMounted = false;
    };
  }, [isOpen, content, isBlog, isEvent]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden"; // Prevent body scroll
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !content) return null;

  // Compute live full URL
  const currentPath = content.path || window.location.pathname;
  const liveShareUrl = buildFullShareUrl(baseUrl, currentPath, utmParams);

  // Handle platform change
  const handleSelectPlatform = (platformId) => {
    setSelectedPlatform(platformId);
    const newDefaults = getDefaultUtmParams(platformId, {
      ...content,
      contentType: isEvent ? "event" : isBlog ? "blog" : "project"
    });
    // Merge existing custom values or apply new platform defaults
    setUtmParams((prev) => ({
      ...prev,
      utm_source: newDefaults.utm_source,
      utm_medium: newDefaults.utm_medium,
      utm_source_platform: newDefaults.utm_source_platform,
      utm_content: newDefaults.utm_content
    }));
    setNoticeMessage("");
  };

  // Handle UTM input change
  const handleUtmChange = (key, value) => {
    setUtmParams((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  // Reset UTM parameters to defaults for current platform
  const handleResetUtm = () => {
    const defaults = getDefaultUtmParams(selectedPlatform, {
      ...content,
      contentType: isEvent ? "event" : isBlog ? "blog" : "project"
    });
    setUtmParams(defaults);
    setNoticeMessage("UTM parameters reset to platform defaults.");
    setNoticeType("info");
    setTimeout(() => setNoticeMessage(""), 3000);
  };

  // Copy URL with fallback
  const copyToClipboard = async (textToCopy) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        return true;
      } else {
        // Fallback for older browsers or insecure contexts
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        textArea.remove();
        return successful;
      }
    } catch (err) {
      console.error("Clipboard copy failed:", err);
      return false;
    }
  };

  // Main sharing action handler
  const handleShareAction = async (platformId) => {
    const targetPlatform = platformId || selectedPlatform;
    const intent = getPlatformShareIntent(targetPlatform, liveShareUrl, shareMessage, content.title, isBlog, isEvent);

    // Track the action in backend with explicit contentType
    const actionType = targetPlatform === "copy_link" || targetPlatform === "instagram" ? "copy_link" : "share_initiated";
    trackShareActionApi({
      contentType: isEvent ? "event" : isBlog ? "blog" : "project",
      projectId: content.id,
      projectTitle: content.title,
      projectPath: currentPath,
      blogId: content.id,
      blogTitle: content.title,
      blogPath: currentPath,
      eventId: content.id,
      eventTitle: content.title,
      eventPath: currentPath,
      platform: targetPlatform,
      actionType,
      utmParams
    });

    if (intent.type === "copy") {
      const copied = await copyToClipboard(liveShareUrl);
      if (copied) {
        setCopySuccess(true);
        setNoticeMessage("Tracking link copied to clipboard successfully! Ready to share. ✨");
        setNoticeType("success");
        setTimeout(() => setCopySuccess(false), 3500);
      } else {
        setNoticeMessage("Could not copy automatically. Please copy the URL from the preview field.");
        setNoticeType("error");
      }
    } else if (intent.type === "copy_notice") {
      // Instagram workflow
      await copyToClipboard(liveShareUrl);
      setCopySuccess(true);
      setNoticeMessage(intent.message);
      setNoticeType("info");
      setTimeout(() => setCopySuccess(false), 4500);
    } else if (intent.type === "link") {
      window.location.href = intent.url;
      setNoticeMessage("Email client opened! You can now send your share email.");
      setNoticeType("success");
    } else if (intent.type === "window") {
      // Open popup window
      const width = 600;
      const height = 550;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      window.open(
        intent.url,
        `Share_${targetPlatform}`,
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`
      );
      setNoticeMessage(`Opening ${targetPlatform.toUpperCase()} sharing window... 🚀`);
      setNoticeType("success");
    }
  };

  return (
    <div className="share-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
      <div className="share-modal-card" onClick={(e) => e.stopPropagation()} ref={modalRef}>
        {/* Header */}
        <div className="share-modal-header">
          <div className="share-modal-title-wrap">
            <span className="share-header-icon">🔗</span>
            <div>
              <h2 id="share-modal-title" className="share-modal-title">
                {isEvent ? "Share Event" : isBlog ? "Share Blog" : "Share Project"}
              </h2>
              <p className="share-modal-subtitle">
                Generate tracked UTM campaign links for <strong>{content.title}</strong>
              </p>
            </div>
          </div>
          <button className="share-modal-close-btn" onClick={onClose} aria-label="Close share dialog" ref={initialFocusRef}>
            <FaTimes />
          </button>
        </div>

        {/* Notice Banner */}
        {noticeMessage && (
          <div className={`share-feedback-toast ${noticeType}`}>
            <span>{noticeType === "success" ? "✅" : noticeType === "error" ? "⚠️" : "ℹ️"}</span>
            <p>{noticeMessage}</p>
          </div>
        )}

        {/* Platform Grid */}
        <div className="share-section">
          <label className="share-section-label">Select Sharing Platform</label>
          <div className="share-platform-grid">
            {PLATFORMS.map((platform) => {
              const isSelected = selectedPlatform === platform.id;
              return (
                <button
                  key={platform.id}
                  className={`share-platform-btn ${isSelected ? "selected" : ""}`}
                  onClick={() => {
                    handleSelectPlatform(platform.id);
                    if (platform.id !== "copy_link") {
                      handleShareAction(platform.id);
                    }
                  }}
                  style={{
                    "--platform-color": platform.color,
                    "--platform-bg": platform.bg
                  }}
                  title={`Share on ${platform.name}`}
                >
                  <span className="platform-icon" style={{ color: platform.color }}>
                    {platform.icon}
                  </span>
                  <span className="platform-name">{platform.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Share Message Input */}
        <div className="share-section">
          <label className="share-section-label" htmlFor="share-message-input">
            Share Message
          </label>
          <textarea
            id="share-message-input"
            className="share-message-textarea"
            rows="2"
            value={shareMessage}
            onChange={(e) => setShareMessage(e.target.value)}
            placeholder="Write a custom share caption..."
          />
        </div>

        {/* Live URL Preview with Copy Button */}
        <div className="share-section">
          <div className="share-preview-header">
            <label className="share-section-label">Generated Campaign Tracking URL</label>
            <span className="share-pill-platform">Source: {utmParams.utm_source || "custom"}</span>
          </div>
          <div className="share-url-box">
            <input
              type="text"
              readOnly
              className="share-url-input"
              value={liveShareUrl}
              onClick={(e) => e.target.select()}
            />
            <button
              className={`share-copy-action-btn ${copySuccess ? "copied" : ""}`}
              onClick={() => handleShareAction("copy_link")}
              title="Copy link to clipboard"
            >
              {copySuccess ? (
                <>
                  <FaCheck /> Copied!
                </>
              ) : (
                <>
                  <FaCopy /> Copy Link
                </>
              )}
            </button>
          </div>
        </div>

        {/* Advanced UTM Campaign Settings Accordion */}
        <div className="share-utm-accordion">
          <button
            type="button"
            className="utm-accordion-toggle"
            onClick={() => setShowAdvancedUtm(!showAdvancedUtm)}
            aria-expanded={showAdvancedUtm}
          >
            <span className="toggle-left">
              <FaSlidersH /> Advanced UTM Campaign Settings
            </span>
            <span className="toggle-arrow">{showAdvancedUtm ? "▲" : "▼"}</span>
          </button>

          {showAdvancedUtm && (
            <div className="utm-accordion-body">
              <div className="utm-header-actions">
                <span className="utm-help-text">
                  Customize campaign tracking parameters for Google Analytics and Admin Dashboard.
                </span>
                <button type="button" className="utm-reset-btn" onClick={handleResetUtm}>
                  Reset Defaults
                </button>
              </div>

              <div className="utm-grid">
                <div className="utm-field">
                  <label>UTM Source (utm_source)</label>
                  <input
                    type="text"
                    placeholder="e.g. linkedin, twitter, newsletter"
                    value={utmParams.utm_source}
                    onChange={(e) => handleUtmChange("utm_source", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>UTM Medium (utm_medium)</label>
                  <input
                    type="text"
                    placeholder="e.g. social, referral, email"
                    value={utmParams.utm_medium}
                    onChange={(e) => handleUtmChange("utm_medium", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>UTM Campaign (utm_campaign)</label>
                  <input
                    type="text"
                    placeholder={isBlog ? "e.g. blog_share, tech_read" : "e.g. portfolio_share, winter_showcase"}
                    value={utmParams.utm_campaign}
                    onChange={(e) => handleUtmChange("utm_campaign", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>Campaign ID (utm_id)</label>
                  <input
                    type="text"
                    placeholder={isBlog ? "e.g. blog_toDoListApp" : "e.g. proj_employeeTaskManager"}
                    value={utmParams.utm_id}
                    onChange={(e) => handleUtmChange("utm_id", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>Campaign Content (utm_content)</label>
                  <input
                    type="text"
                    placeholder="e.g. header_button, detail_card"
                    value={utmParams.utm_content}
                    onChange={(e) => handleUtmChange("utm_content", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>Campaign Term (utm_term)</label>
                  <input
                    type="text"
                    placeholder={isBlog ? "e.g. portfolio_blog, tech_guide" : "e.g. portfolio_project, web_dev"}
                    value={utmParams.utm_term}
                    onChange={(e) => handleUtmChange("utm_term", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>Source Platform (utm_source_platform)</label>
                  <input
                    type="text"
                    placeholder="e.g. linkedin_feed, x_web"
                    value={utmParams.utm_source_platform}
                    onChange={(e) => handleUtmChange("utm_source_platform", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>Creative Format (utm_creative_format)</label>
                  <input
                    type="text"
                    placeholder="e.g. post, link_card, story"
                    value={utmParams.utm_creative_format}
                    onChange={(e) => handleUtmChange("utm_creative_format", e.target.value)}
                  />
                </div>

                <div className="utm-field">
                  <label>Marketing Tactic (utm_marketing_tactic)</label>
                  <input
                    type="text"
                    placeholder="e.g. peer_sharing, showcase"
                    value={utmParams.utm_marketing_tactic}
                    onChange={(e) => handleUtmChange("utm_marketing_tactic", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="share-modal-footer">
          <button className="share-footer-btn share-cancel-btn" onClick={onClose}>
            Close
          </button>
          <button
            className="share-footer-btn share-primary-action-btn"
            onClick={() => handleShareAction(selectedPlatform)}
          >
            {selectedPlatform === "copy_link" ? (
              <>
                <FaCopy /> {isBlog ? "Copy Blog Link" : "Copy Project Link"}
              </>
            ) : selectedPlatform === "instagram" ? (
              <>
                <FaInstagram /> Copy for Instagram
              </>
            ) : (
              <>
                <FaExternalLinkAlt /> Share via {PLATFORMS.find((p) => p.id === selectedPlatform)?.name || "Platform"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
